import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { GetJson, PutJson, PostJson, DeleteJson } from '@awp/helpers/fetchReact';
import { REST_API, Permissions, FORM_MODE, STATE_STATUS, SENSOR_PLACEMENT_CONFIG } from '@awp/constants';
import { Row, Col, Card, Container } from 'react-bootstrap';
import { FormInput, FormTextArea } from '@awp/components/Common/AwpFormInputs';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { isValueEmpty, formatJson, isJsonString } from '@awp/helpers/utilsReact';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import PlacementTemplateList from '@awp/components/SensorTypeAdmin/PlacementTemplateList';
import { ObservationDataPrecedence } from '@components/Common/ObservationDataPrecedence';
import { getObservationData } from '@graphs/Presets/Helpers/PresetHelper';

import {
  setEditing,
  setTemplate,
  setOrignTemplate,
  setTotId,
  setSaveTemplateStatus,
  setSaveTemplateError,
  fetchPlacementTemplatePacket,
} from '@awp/stores/placementTemplatePacketSlice';
import isEqual from 'lodash/isEqual';
import SensorTypeService, { INIT_PRECEDENCE } from '@components/SensorTypeAdmin/SensorTypeService';

const SensorTypeEdit = ({ setMode, mode, sensorTypeId, data, setData, setLoading, loading }) => {
  const dispatch = useDispatch();

  const [sensorTypeData, setSensorTypeData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [observationData, setObservationData] = useState([]);
  const [observations, setObservations] = useState([INIT_PRECEDENCE]);
  const [precedenceErrorMessage, setPrecedenceErrorMessage] = useState('');
  const [initialDto, setInitialDto] = useState({});

  const crudService = new SensorTypeService(mode);
  const { readOnly, title, buttonLabel, successMessage, entityName } = crudService.crud;

  useEffect(() => {
    fetchData();
    if (mode === FORM_MODE.CREATE) {
      setSensorTypeData({ Name: '', Description: '' });
      setInitialDto(
        crudService.mapInitialValues({
          entity: {
            ...sensorTypeData,
            Precedences: observations,
          },
        }),
      );

      return;
    }
    if (mode === FORM_MODE.VIEW) {
      cleanPlacementList();
      dispatch(fetchPlacementTemplatePacket(sensorTypeId));
    }
    setLoading(true);
    const url = REST_API.SENSOR_TYPES + sensorTypeId;
    GetJson(url)
      .then((response) => response.json())
      .then((st) => {
        setSensorTypeData(st);
        st.Precedences = st.Precedences != null && st.Precedences.length > 0 ? [...st.Precedences] : [INIT_PRECEDENCE];
        setObservations(st.Precedences);
        setInitialDto(crudService.mapInitialValues({ entity: st }));
        setLoading(false);
      });
  }, []);

  const fetchData = async () => {
    try {
      let data = [];
      data = await getObservationData();
      setObservationData(data);
    } catch (error) {
      console.error('Unable to fetchData for Observation Type dropdown');
    }
  };
  const cleanPlacementList = () => {
    dispatch(setSaveTemplateStatus(STATE_STATUS.IDLE));

    dispatch(setEditing(null));
    //template
    dispatch(setTemplate(null));
    dispatch(setOrignTemplate(null));
    dispatch(setTotId(SENSOR_PLACEMENT_CONFIG.TOT_ID));
    dispatch(setSaveTemplateError(null));
  };
  const updateSensorType = (setSubmitting, resetForm, setErrors, sensorTypeDto) => {
    const url = REST_API.SENSOR_TYPES + sensorTypeDto.Id;

    PutJson(url, sensorTypeDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const st = await response.json();
          st.Precedences = st.Precedences.length == 0 ? [...st.Precedences, ...[INIT_PRECEDENCE]] : st.Precedences;
          setSensorTypeData(st);
          updateData(st);
          setObservations(st.Precedences);

          const updatedDto = crudService.mapInitialValues({ entity: st });
          setInitialDto(updatedDto);
          resetForm({
            values: updatedDto,
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ SensorType: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createSensorType = (setSubmitting, resetForm, setErrors, sensorTypeDto) => {
    const url = REST_API.SENSOR_TYPES;

    PostJson(url, sensorTypeDto)
      .then(async (response) => {
        if (response.ok) {
          const st = await response.json();
          st.Precedences = st.Precedences.length == 0 ? [...st.Precedences, ...[INIT_PRECEDENCE]] : st.Precedences;
          setObservations(st.Precedences);
          setSensorTypeData(st);
          updateData(st);
          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);
          cleanPlacementList();
          dispatch(fetchPlacementTemplatePacket(st.Id));

          const updatedDto = crudService.mapInitialValues({ entity: st });
          setInitialDto(updatedDto);
          resetForm({
            values: updatedDto,
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ SensorType: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteSensorType = () => {
    const id = sensorTypeData.Id;
    const url = REST_API.SENSOR_TYPES + id;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          setSensorTypeData({});
          removeData(id);
          toast.success(`${entityName} has been deleted`);
          setMode(FORM_MODE.LIST);
        } else if (response.status === 412) {
          const errors = await response.json();
          if (errors.entity) setEntityErrors({ SensorType: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    setPrecedenceErrorMessage('');
    const precedences = observations.filter((x) => x.ObservationTypeId != null);

    const sensorTypeDto = {
      ...sensorTypeData,
      Name: values.name,
      DisplayName: values.displayName,
      Description: values.description,
      PlacementAttributeSchema: values.sensorPlacementMetadata,
      Precedences: precedences,
    };

    if (mode === FORM_MODE.EDIT) {
      updateSensorType(setSubmitting, resetForm, setErrors, sensorTypeDto);
    } else {
      createSensorType(setSubmitting, resetForm, setErrors, sensorTypeDto);
    }
  };

  const updateData = (st) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === st.Id);
      if (index < 0) return;
      data[index] = st;
      setData([...data]);
    } else {
      setData([...data, st]);
    }
  };
  const removeData = (Id) => {
    if (mode === FORM_MODE.EDIT) {
      let _data = [...data];
      let _removedData = [];
      _data.map((o) => {
        if (o.Id !== Id) {
          _removedData.push(o);
        }
      });
      setData([..._removedData]);
    }
  };
  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleAddObservation = () => {
    setObservations([...observations, ...[INIT_PRECEDENCE]]);
  };

  const handleDeleteObservation = (index) => {
    if (observations.length == 1 && observations[0].ObservationTypeId == null) {
      return;
    }
    const newObservations = observations.filter((_, idx) => idx !== index);
    newObservations.length == 0 ? setObservations([INIT_PRECEDENCE]) : setObservations(newObservations);
  };

  const handleObservationTypeChange = (index, newTypeId) => {
    const updatedObservations = observations.map((obs, idx) =>
      idx === index ? { ...obs, ObservationTypeId: newTypeId, SensorTypeId: sensorTypeData.Id } : obs,
    );
    setObservations(updatedObservations);
  };

  const handlePrecedenceChange = (index, newPrecedence) => {
    const updatedObservations = observations.map((obs, idx) =>
      idx === index
        ? {
            ...obs,
            Relative_Precedence_Number: isValueEmpty(newPrecedence) ? 0.5 : parseFloat(newPrecedence),
            SensorTypeId: sensorTypeData.Id,
          }
        : obs,
    );
    setObservations(updatedObservations);
  };

  return (
    <>
      {loading ? (
        <Container>
          <AwpSpinner />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={{
              ...crudService.mapInitialValues({ entity: sensorTypeData }),
            }}
            validateOnMount={true}
            validationSchema={Yup.object({
              name: Yup.string().max(100, 'Maximum 100 characters are allowed').required('Name is a required field'),
              displayName: Yup.string()
                .max(45, 'Maximum 45 characters are allowed')
                .required('Display Name is a required field'),
              description: Yup.string().max(250, 'Maximum 250 characters are allowed'),
              sensorPlacementMetadata: Yup.string()
                .max(2000, 'Maximum 2000 characters are allowed')
                .test('isJson', 'Metadata must be a valid JSON', (value) => {
                  if (!value) return true;
                  if (value.trim() === '') return true;
                  return isJsonString(value);
                }),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => {
              const { PersistentPrecedentFilters, ...propsValues } = props.values;
              const myIsPristine = isEqual(propsValues, initialDto) && isEqual(initialDto.Precedences, observations);

              return (
                <Form>
                  <span className="awp-text-btn" onClick={() => setMode(FORM_MODE.LIST)}>
                    List Sensor Types
                  </span>
                  <span className="mx-3">&gt;</span>
                  <span>{isValueEmpty(sensorTypeData.Name) ? title : sensorTypeData.Name}</span>
                  <Card border="light" className="page-card">
                    <Card.Header>
                      <Row className="mt-4"></Row>
                      {crudService.crud.getHeader()}
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={5} className="mb-4">
                          <FormInput
                            label="Name (required)"
                            instruction="(100 Characters max)"
                            readOnly={readOnly || sensorTypeData.IsProtected === 'Y'}
                            name="name"
                            id="name"
                          />
                        </Col>
                        <Col sm={5} className="mb-4">
                          <FormInput
                            label="Display Name (required)"
                            instruction="(45 Characters max)"
                            readOnly={readOnly || sensorTypeData.IsProtected === 'Y'}
                            name="displayName"
                            id="displayName"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={8} className="mb-4">
                          <FormTextArea
                            readOnly={readOnly}
                            label="Description"
                            instruction="(250 Characters max)"
                            id="description"
                            name="description"
                            rows={3}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={8} className="mb-4">
                          <FormTextArea
                            readOnly={readOnly}
                            label="Sensor Placement Metadata"
                            instruction="(2000 Characters max)"
                            id="sensorPlacementMetadata"
                            name="sensorPlacementMetadata"
                            rows={3}
                            onBlur={(e) => {
                              props.setFieldValue('sensorPlacementMetadata', formatJson(e.target.value));
                              props.setFieldTouched('sensorPlacementMetadata');
                            }}
                          />
                        </Col>
                      </Row>
                      <ObservationDataPrecedence
                        observationData={observationData}
                        errorMessage={precedenceErrorMessage}
                        setErrorMessage={setPrecedenceErrorMessage}
                        persistentPrecedentFilters={observations}
                        onAdd={handleAddObservation}
                        onDelete={(index) => {
                          props.setFieldValue(`PersistentPrecedentFilters[${index}].ObservationTypeId`, null);
                          props.setFieldValue(`PersistentPrecedentFilters[${index}].Relative_Precedence_Number`, 0.5);
                          props.setFieldTouched(`PersistentPrecedentFilters[${index}].ObservationTypeId`, true, false);
                          handleDeleteObservation(index);
                        }}
                        onObservationTypeChange={(index, id) => {
                          props.setFieldValue(`PersistentPrecedentFilters[${index}].ObservationTypeId`, id);
                          props.setFieldTouched(`PersistentPrecedentFilters[${index}].ObservationTypeId`, true, false);
                          handleObservationTypeChange(index, id);
                        }}
                        onPrecedenceChange={(index, id) => {
                          handlePrecedenceChange(index, id);
                        }}
                        readOnly={mode !== FORM_MODE.VIEW}
                      />
                    </Card.Body>
                    <Card.Footer>
                      {!showDeleteConfirm ? (
                        <Row className="my-2">
                          <AwpFormActionButtons
                            title=" "
                            id={2}
                            entityName="Sensor Type"
                            buttonLabel={buttonLabel}
                            mode={mode}
                            setMode={setMode}
                            onClickDelete={onClickDelete}
                            permissions={[Permissions.WriteSensorType]}
                            readOnly={readOnly}
                            isAlignLeft={true}
                            btnCancelName={'Close'}
                            props={props}
                            isPristine={myIsPristine}
                          />
                        </Row>
                      ) : (
                        <Row className="my-2">
                          <AwpYesNoDialog
                            action={deleteSensorType}
                            setShowYesNoConfirm={setShowDeleteConfirm}
                            props={props}
                          />
                        </Row>
                      )}
                    </Card.Footer>
                  </Card>
                </Form>
              );
            }}
          </Formik>
          {mode === FORM_MODE.VIEW && (
            <Row>
              <Col sm={12} className="mb-4">
                <PlacementTemplateList sensorTypeData={sensorTypeData} />
              </Col>
            </Row>
          )}
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default SensorTypeEdit;
